import React, { useState, useEffect, useRef } from 'react';
import { Clock, Star, ChevronRight, BookOpen, FileText, Youtube } from 'lucide-react';
import { MathRenderer } from '../math/MathRenderer';

const NoteSectionCard = ({ note, minReadingTime = 30, onComplete, isTriggered = false, styling }) => {
  const [timeRemaining, setTimeRemaining] = useState(minReadingTime);
  const [timerDone, setTimerDone] = useState(false);
  const [rating, setRating] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const startTimeRef = useRef(Date.now());
  const intervalRef = useRef(null);

  useEffect(() => {
    startTimeRef.current = Date.now();
    setTimeRemaining(minReadingTime);
    setTimerDone(minReadingTime <= 0);
    setRating(0);
    setTimeSpent(0);

    if (minReadingTime > 0) {
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const remaining = Math.max(0, minReadingTime - elapsed);
        setTimeRemaining(remaining);
        setTimeSpent(elapsed);
        if (remaining === 0) {
          setTimerDone(true);
          clearInterval(intervalRef.current);
        }
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [note?.id, minReadingTime]);

  const handleContinue = () => {
    const totalTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    onComplete(rating, totalTime);
  };

  const canContinue = timerDone && rating > 0;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const renderNoteContent = () => {
    if (!note) return null;

    const noteType = note.type || 'text';

    if (noteType === 'youtube' && note.url) {
      const videoId = extractYouTubeId(note.url);
      if (videoId) {
        return (
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title={note.title || 'Video'}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }
    }

    if (noteType === 'pdf' && note.url) {
      return (
        <div className="w-full h-[60vh] rounded-lg overflow-hidden border border-slate-600">
          <iframe
            src={note.url}
            title={note.title || 'Document'}
            className="w-full h-full"
          />
        </div>
      );
    }

    // Default: text note
    return (
      <div className="prose prose-invert prose-sm max-w-none text-slate-200">
        <MathRenderer content={note.text || ''} enableMarkdown={true} enableMath={true} />
      </div>
    );
  };

  const getNoteTypeIcon = () => {
    const noteType = note?.type || 'text';
    if (noteType === 'youtube') return <Youtube className="h-5 w-5" />;
    if (noteType === 'pdf') return <FileText className="h-5 w-5" />;
    return <BookOpen className="h-5 w-5" />;
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className={`px-5 py-4 border-b border-slate-700 flex items-center justify-between ${
        isTriggered ? 'bg-amber-500/10' : 'bg-indigo-500/10'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isTriggered ? 'bg-amber-500/20 text-amber-400' : 'bg-indigo-500/20 text-indigo-400'}`}>
            {getNoteTypeIcon()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white">{note?.title || 'Study Note'}</h3>
              {isTriggered && (
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                  Review Material
                </span>
              )}
            </div>
            {(note?.subject || note?.topic) && (
              <p className="text-xs text-slate-400 mt-0.5">
                {[note.subject, note.grade, note.topic].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Timer */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
          timerDone
            ? 'bg-green-500/20 text-green-400'
            : 'bg-slate-700 text-slate-300'
        }`}>
          <Clock className="h-4 w-4" />
          {timerDone ? 'Ready' : formatTime(timeRemaining)}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {renderNoteContent()}
      </div>

      {/* Rating + Continue */}
      <div className="px-5 py-4 border-t border-slate-700 bg-slate-800/80">
        {!timerDone ? (
          <div className="text-center text-slate-400 text-sm">
            <Clock className="h-5 w-5 inline mr-2" />
            Please read the material. Continue will be available in {formatTime(timeRemaining)}.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Rating */}
            <div>
              <p className="text-sm text-slate-300 mb-2 text-center">
                How well do you understand this material?
              </p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    onClick={() => setRating(value)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                      rating === value
                        ? 'border-indigo-400 bg-indigo-500/20 text-indigo-300'
                        : 'border-slate-600 bg-slate-700/30 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                    }`}
                  >
                    <Star className={`h-4 w-4 ${rating >= value ? 'fill-current' : ''}`} />
                    {value}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1 px-2">
                <span>Not at all</span>
                <span>Very well</span>
              </div>
            </div>

            {/* Continue Button */}
            <div className="flex justify-center">
              <button
                onClick={handleContinue}
                disabled={!canContinue}
                className="px-6 py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: canContinue ? (styling?.primaryColor || '#6366f1') : undefined }}
              >
                Continue
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export default NoteSectionCard;
